import { useState, FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
  onSubmit: (email: string) => void | Promise<void>;
  isPending?: boolean;
  isSuccess?: boolean;
}

export function ForgotPasswordForm({
  onBackToLogin,
  onSubmit,
  isPending,
  isSuccess,
}: ForgotPasswordFormProps) {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");

  const isEmail = (v: string) => /\S+@\S+\.\S+/.test(v);
  const canSubmit = isEmail(email) && !isPending;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    await onSubmit(email);
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-full bg-brand-accent">
          <KeyRound className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">
          {t("forgotPasswordTitle")}
        </h2>
      </div>

      {isSuccess ? (
        <div className="space-y-4">
          <p className="text-sm text-green-600">{t("resetEmailSent")}</p>
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-sm text-primary hover:text-primary/80 transition-colors duration-200 hover:underline"
            >
              {t("backToLogin")}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <p className="text-sm text-muted-foreground">
            {t("forgotPasswordDescription")}
          </p>

          <div className="space-y-2">
            <Label
              htmlFor="resetEmail"
              className="text-sm font-medium text-foreground"
            >
              {t("email")}
            </Label>
            <Input
              id="resetEmail"
              type="email"
              placeholder={t("emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-11 px-4 py-3 text-[15px] placeholder:text-muted-foreground/70 rounded-xl transition-all duration-200 focus:shadow-lg focus:shadow-primary/10"
            />
            {email.length > 0 && !isEmail(email) && (
              <p className="text-xs text-red-600">{t("invalidEmail")}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={!canSubmit}
            aria-busy={isPending}
            className="w-full h-12 bg-brand-accent disabled:opacity-60 disabled:cursor-not-allowed hover:bg-brand-accent/90 text-white font-medium text-base transition-all duration-300 hover:shadow-lg hover:shadow-brand-accent/20"
          >
            {isPending ? t("processing") : t("sendResetLink")}
          </Button>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={onBackToLogin}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
            >
              {t("backToLogin")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
